import { EventType } from "../enums/enum";
import { SiteLanguage } from "../i18n";
import { approvedKateArticleVi } from "./kateArticleVi";

export type EventArticleSlug = "kate" | "ramawan" | "rija-nagar";

export interface EventArticleSection {
  heading: string;
  body: readonly string[];
}

export interface EventArticleSource {
  label: string;
  note?: string;
  href?: string;
}

export interface EventArticle {
  slug: EventArticleSlug;
  eventIds: readonly EventType[];
  title: string;
  shortTitle: string;
  summary: string;
  introduction: readonly string[];
  sections: readonly EventArticleSection[];
  sources: readonly EventArticleSource[];
  approvedMarkdown?: string;
  seo: {
    title: string;
    description: string;
    canonicalUrl: string;
    imageUrl: string;
  };
}

export const eventArticleSlugs: readonly EventArticleSlug[] = ["kate", "ramawan", "rija-nagar"];

const canonicalBaseUrl = "https://www.sakawi.com";
const socialImageUrl = `${canonicalBaseUrl}/og-image.png`;

export const eventArticleSlugByEventType: Partial<Record<EventType, EventArticleSlug>> = {
  KatePaleiHamuTanran: "kate",
  KateAngaokBimong: "kate",
  TamaRicaowRamawan: "ramawan",
  TalaihAekRamawan: "ramawan",
  MukTrun: "ramawan",
  OngTrun: "ramawan",
  RijaNagar: "rija-nagar"
};

const sharedViSources: readonly EventArticleSource[] = [
  {
    label: "Dữ liệu sự kiện Sakawi",
    note: "Tên sự kiện, loại lịch và mô tả ngắn đang được dùng trong ứng dụng Sakawi."
  },
  {
    label: "Tài liệu nền tảng Sakawi",
    href: "/documents",
    note: "Các trang giải thích Sakawi Cham, Sakawi Awal và nguyên tắc lịch trong sản phẩm."
  }
];

const sharedEnSources: readonly EventArticleSource[] = [
  {
    label: "Sakawi event data",
    note: "Event names, calendar type, and short descriptions used by the Sakawi app."
  },
  {
    label: "Sakawi foundational documents",
    href: "/documents",
    note: "Product documents explaining Sakawi Cham, Sakawi Awal, and calendar-rule context."
  }
];

export const eventArticles: Record<SiteLanguage, readonly EventArticle[]> = {
  vi: [
    {
      slug: "kate",
      eventIds: ["KateAngaokBimong", "KatePaleiHamuTanran"],
      title: "Lễ Katé của người Chăm",
      shortTitle: "Katê",
      summary: "",
      introduction: [],
      sections: [],
      sources: [],
      approvedMarkdown: approvedKateArticleVi,
      seo: {
        title: "Lễ Katé của người Chăm | Sakawi",
        description: "Tìm hiểu lễ Katé (Katê) trong lịch Chăm, Hamu Tanran, Po Inâ Nâgar và mối quan hệ văn hóa giữa người Chăm với người Raglai.",
        canonicalUrl: `${canonicalBaseUrl}/events/kate`,
        imageUrl: socialImageUrl
      }
    },
    {
      slug: "ramawan",
      eventIds: ["TamaRicaowRamawan", "TalaihAekRamawan", "MukTrun", "OngTrun"],
      title: "Ramawan",
      shortTitle: "Ramawan",
      summary: "Tìm hiểu Ramawan trong ngữ cảnh Sakawi Awal/Bani, như một giai đoạn chay tịnh và các mốc liên quan trong lịch Awal.",
      introduction: [
        "Ramawan trong Sakawi được đặt trong hệ Sakawi Awal, là phần lịch gắn với cộng đồng Chăm Bani.",
        "Dữ liệu Sakawi hiện mô tả Tamâ ricaow Ramâwan là thời điểm bắt đầu tháng chay tịnh Ramâwan, cùng các mốc liên quan như Muk trun, Ong trun và Talaih aek Ramâwan."
      ],
      sections: [
        {
          heading: "Thời gian và các mốc Sakawi Awal",
          body: [
            "Ramawan được Sakawi xử lý theo lịch Awal, không phải như một ngày Dương lịch cố định.",
            "Bài viết này dùng các mốc đã có trong dữ liệu Sakawi: bắt đầu Ramawan, các ngày liên quan trong tháng Ramawan, và mốc kết thúc Ramawan."
          ]
        },
        {
          heading: "Ý nghĩa và cách diễn đạt thận trọng",
          body: [
            "Sakawi ghi nhận Ramawan là tháng chay tịnh trong ngữ cảnh Awal/Bani. Vì vậy, trang này tránh rút gọn Ramawan thành một mô tả Hồi giáo phổ quát.",
            "Các thuật ngữ và thực hành cụ thể của cộng đồng Chăm Awal/Bani cần được giữ đúng ngữ cảnh và tiếp tục được rà soát bởi người có thẩm quyền văn hóa hoặc tôn giáo."
          ]
        },
        {
          heading: "Hoạt động và khác biệt cộng đồng",
          body: [
            "Ở mức dữ liệu sản phẩm, Sakawi xác định các mốc Ramawan để người dùng theo dõi thời gian và mở ngày tương ứng trong lịch.",
            "Bài viết chưa mô tả chi tiết nghi thức tại từng palei hoặc từng cộng đồng. Phần đó cần nguồn cộng đồng được phê duyệt trước khi mở rộng."
          ]
        }
      ],
      sources: sharedViSources,
      seo: {
        title: "Ramawan - Tìm hiểu lễ Ramawan của cộng đồng Chăm Awal | Sakawi",
        description: "Tìm hiểu Ramawan trong Sakawi Awal/Bani: thời gian, ý nghĩa chay tịnh, các mốc liên quan và lưu ý về ngữ cảnh cộng đồng.",
        canonicalUrl: `${canonicalBaseUrl}/events/ramawan`,
        imageUrl: socialImageUrl
      }
    },
    {
      slug: "rija-nagar",
      eventIds: ["RijaNagar"],
      title: "Rija Nagar",
      shortTitle: "Rija Nagar",
      summary: "Tìm hiểu Rija Nagar như một lễ hội xứ sở trong dữ liệu Sakawi, với thời gian được tính theo lịch Cham/Ahier và bối cảnh vùng.",
      introduction: [
        "Rija Nagar được dữ liệu Sakawi mô tả là lễ hội xứ sở và là một sự kiện thuộc lịch Cham/Ahier.",
        "Trang này đặt Rija Nagar trong vai trò một mốc văn hóa - lịch pháp để người đọc hiểu bối cảnh trước khi mở ngày cụ thể trong Sakawi."
      ],
      sections: [
        {
          heading: "Thời gian trong Sakawi",
          body: [
            "Mô tả sự kiện hiện có của Sakawi ghi Rija Nagar là lễ hội xứ sở vào thứ Năm đầu tiên của năm.",
            "Bộ tính lịch của Sakawi cũng có logic khu vực và ngữ cảnh tháng Awal khi xác định ngày hiển thị. Vì vậy, ngày Dương lịch cần được xem theo năm và khu vực đang chọn."
          ]
        },
        {
          heading: "Ý nghĩa và bối cảnh vùng",
          body: [
            "Sakawi hiện dùng cách diễn đạt thận trọng: Rija Nagar là ngày lễ/việc Chăm theo vùng.",
            "Cách hiểu, cách gọi và thực hành có thể cần được đối chiếu theo cộng đồng, palei và nguồn truyền thống liên quan."
          ]
        },
        {
          heading: "Hoạt động và ghi chú biên tập",
          body: [
            "Bản MVP chưa mở rộng mô tả nghi lễ chi tiết cho Rija Nagar vì kho nội dung được phê duyệt trong dự án còn ngắn.",
            "Các mô tả sâu hơn về hoạt động, trình tự và vai trò của từng nhóm trong cộng đồng nên được bổ sung sau khi có nguồn biên tập đáng tin cậy."
          ]
        }
      ],
      sources: sharedViSources,
      seo: {
        title: "Rija Nagar - Tìm hiểu nghi lễ Rija Nagar của người Chăm | Sakawi",
        description: "Tìm hiểu Rija Nagar trong Sakawi: lễ hội xứ sở, thời gian theo lịch Cham/Ahier, bối cảnh vùng và lưu ý biên tập.",
        canonicalUrl: `${canonicalBaseUrl}/events/rija-nagar`,
        imageUrl: socialImageUrl
      }
    }
  ],
  en: [
    {
      slug: "kate",
      eventIds: ["KateAngaokBimong", "KatePaleiHamuTanran"],
      title: "Katê Festival",
      shortTitle: "Katê",
      summary: "A concise Event Knowledge page for Katê, an important Cham/Ahier calendar event tracked by Sakawi.",
      introduction: [
        "Katê is one of the main Cham/Ahier calendar events tracked by Sakawi.",
        "Current Sakawi data includes named Katê contexts such as Katê at the tower and Katê in Hamu Tanran village. This first article is intentionally conservative until community-reviewed cultural detail is added."
      ],
      sections: [
        {
          heading: "Timing and calendar context",
          body: [
            "Katê is not tied to a fixed Gregorian date. Sakawi resolves the displayed Gregorian date from the Cham/Ahier calendar and the selected calendar region.",
            "Official yearly calendars should still be checked against the relevant councils or religious authorities."
          ]
        },
        {
          heading: "Meaning and scope",
          body: [
            "Sakawi describes Katê as a major Cham festival. This page treats it as an important cultural and calendar marker for readers who want more context after seeing the event in the app.",
            "It does not claim that every community or palei observes Katê in the same way."
          ]
        },
        {
          heading: "Community notes",
          body: [
            "The current product data distinguishes multiple Katê names and contexts.",
            "More detailed descriptions of ritual order, location-specific practice, and terminology should be reviewed with knowledgeable community editors before publication expansion."
          ]
        }
      ],
      sources: sharedEnSources,
      seo: {
        title: "Katê Festival - Meaning, Timing, and Cham Cultural Context | Sakawi",
        description: "Learn about Katê in Sakawi: an important Cham/Ahier calendar event, its timing context, and careful notes on local variation.",
        canonicalUrl: `${canonicalBaseUrl}/events/kate`,
        imageUrl: socialImageUrl
      }
    },
    {
      slug: "ramawan",
      eventIds: ["TamaRicaowRamawan", "TalaihAekRamawan", "MukTrun", "OngTrun"],
      title: "Ramawan",
      shortTitle: "Ramawan",
      summary: "A careful introduction to Ramawan in the Sakawi Awal/Bani context, focused on the Awal calendar markers used by Sakawi.",
      introduction: [
        "In Sakawi, Ramawan belongs to the Sakawi Awal calendar context associated with the Cham Bani community.",
        "The current event data identifies Tamâ ricaow Ramâwan as the beginning of the Ramawan fasting month, along with related markers such as Muk trun, Ong trun, and Talaih aek Ramâwan."
      ],
      sections: [
        {
          heading: "Timing in Sakawi Awal",
          body: [
            "Ramawan is handled through the Awal calendar rather than as a fixed Gregorian date.",
            "This article follows the Sakawi event markers already present in the product data: the beginning of Ramawan, related dates during the observance period, and the closing marker."
          ]
        },
        {
          heading: "Meaning and careful wording",
          body: [
            "Sakawi records Ramawan as a fasting month in an Awal/Bani context. This page therefore avoids reducing Ramawan to a generic description of Ramadan.",
            "Specific religious terminology and local practice should be reviewed by appropriate Cham Awal/Bani cultural or religious editors."
          ]
        },
        {
          heading: "Community notes",
          body: [
            "For the MVP, Sakawi Web explains the event markers and calendar context without claiming detailed ritual guidance.",
            "Future versions can expand this section after approved community source material is available."
          ]
        }
      ],
      sources: sharedEnSources,
      seo: {
        title: "Ramawan - Understanding the Cham Awal Ramawan Observance | Sakawi",
        description: "Learn about Ramawan in the Sakawi Awal/Bani context, including calendar timing, fasting-month significance, and careful community notes.",
        canonicalUrl: `${canonicalBaseUrl}/events/ramawan`,
        imageUrl: socialImageUrl
      }
    },
    {
      slug: "rija-nagar",
      eventIds: ["RijaNagar"],
      title: "Rija Nagar",
      shortTitle: "Rija Nagar",
      summary: "A concise introduction to Rija Nagar as a regional Cham/Ahier observance tracked by Sakawi.",
      introduction: [
        "Sakawi event data describes Rija Nagar as a regional Cham observance and a Cham/Ahier calendar event.",
        "This page provides careful background for readers who want cultural context before opening the event date in Sakawi."
      ],
      sections: [
        {
          heading: "Timing in Sakawi",
          body: [
            "The current Sakawi description identifies Rija Nagar as an observance connected with the first Thursday of the year.",
            "The calendar engine also accounts for regional and Awal-month context when resolving the displayed date, so the Gregorian date should be read through the selected region."
          ]
        },
        {
          heading: "Meaning and regional context",
          body: [
            "The Vietnamese source text describes Rija Nagar as a 'lễ hội xứ sở', while the English product copy keeps the wording broad as a regional Cham observance.",
            "Specific interpretations and practices should be checked against community, palei, and traditional-authority context."
          ]
        },
        {
          heading: "Editorial note",
          body: [
            "This MVP does not expand into detailed ritual description because the approved project material is still short.",
            "Future content should add reviewed detail about activities, order, and terminology."
          ]
        }
      ],
      sources: sharedEnSources,
      seo: {
        title: "Rija Nagar - Cham Observance, Timing, and Regional Context | Sakawi",
        description: "Learn about Rija Nagar in Sakawi: a regional Cham/Ahier observance, its timing context, and notes on local variation.",
        canonicalUrl: `${canonicalBaseUrl}/events/rija-nagar`,
        imageUrl: socialImageUrl
      }
    }
  ]
};

export function getEventArticleBySlug(language: SiteLanguage, slug: string | undefined) {
  return eventArticles[language].find((article) => article.slug === slug);
}

export function getArticleSlugForEventType(eventType: EventType | undefined) {
  return eventType ? eventArticleSlugByEventType[eventType] : undefined;
}

export function hasEventArticle(eventType: EventType | undefined) {
  return Boolean(getArticleSlugForEventType(eventType));
}
